/**
 * File: src/pages/privacy-page.js
 * Mô tả: Trang Chính Sách Quyền Riêng Tư (Privacy Policy) chuẩn Google Play Store cho tựa game Ant Clash.
 * Liên quan: src/app-router.js, src/i18n/i18n-service.js
 */

import { createElement } from '../utils/dom.js';
import { getLanguage } from '../i18n/i18n-service.js';

export const renderPrivacyPage = (gameSlug = 'ant-clash') => {
  const container = createElement('div', {
    className: 'privacy-page-container',
    attributes: { style: 'padding-top: calc(72px + 2.5rem); padding-bottom: 4rem; background: var(--bg-primary); min-height: 80vh;' },
  });

  const inner = createElement('div', { className: 'container', attributes: { style: 'max-width: 860px;' } });
  const lang = getLanguage();

  const isEn = lang === 'en';

  inner.innerHTML = `
    <div style="background: var(--bg-secondary); padding: 2.5rem; border-radius: var(--radius-lg); border: var(--border-glass);">
      <div style="font-size: 0.85rem; color: var(--color-accent); font-weight: 700; text-transform: uppercase; margin-bottom: 0.5rem;">
        ${isEn ? 'Legal & Compliance Document' : 'Tài Liệu Pháp Lý & Tuân Thủ'}
      </div>
      <h1 style="font-size: 2.25rem; margin-bottom: 0.5rem;">
        ${isEn ? 'Privacy Policy for Ant Clash' : 'Chính Sách Quyền Riêng Tư Game Ant Clash'}
      </h1>
      <p style="font-size: 0.85rem; color: var(--color-text-dim); margin-bottom: 2rem;">
        ${isEn ? 'Last Updated: August 9, 2026 • Developer: SNT Games' : 'Cập nhật lần cuối: Ngày 09 tháng 8 năm 2026 • Nhà phát triển: SNT Games'}
      </p>

      <div style="line-height: 1.8; color: var(--color-text-muted); font-size: 0.95rem;">
        <h3 style="color: #fff; margin-top: 1.5rem; margin-bottom: 0.5rem;">1. Introduction</h3>
        <p>
          ${
            isEn
              ? 'SNT Games ("we", "our", or "us") respects your privacy. This Privacy Policy explains how we handle information when you download, install, and play <strong>Ant Clash</strong> on mobile devices or through web platforms.'
              : 'SNT Games ("chúng tôi") tôn trọng quyền riêng tư của bạn. Chính sách quyền riêng tư này giải thích cách chúng tôi xử lý thông tin khi bạn tải xuống, cài đặt và chơi game <strong>Ant Clash</strong> trên các thiết bị di động hoặc nền tảng web.'
          }
        </p>

        <h3 style="color: #fff; margin-top: 1.5rem; margin-bottom: 0.5rem;">2. Information We Collect</h3>
        <p>
          ${
            isEn
              ? 'Ant Clash is designed to minimize data collection. We do not require personal account creation to play. The types of data processed include:'
              : 'Game Ant Clash được thiết kế để tối giản việc thu thập dữ liệu. Bạn không cần đăng ký tài khoản cá nhân để chơi. Các dữ liệu xử lý bao gồm:'
          }
        </p>
        <ul style="padding-left: 1.5rem; margin-top: 0.5rem;">
          <li><strong>${isEn ? 'Local Game Save Data:' : 'Dữ liệu lưu game cục bộ:'}</strong> ${
            isEn
              ? 'Campaign level progress, unlocked ant cards, resin currency balance, and star achievements stored locally on your device.'
              : 'Tiến trình màn chơi, thẻ kiến đã mở khóa, số dư nhựa và thành tích sao được lưu cục bộ trên thiết bị của bạn.'
          }</li>
          <li><strong>${isEn ? 'Technical Device Information:' : 'Thông tin kỹ thuật thiết bị:'}</strong> ${
            isEn
              ? 'Non-personally identifiable technical details such as OS version, device model, screen resolution, and graphics API capability for game optimization.'
              : 'Các thông tin kỹ thuật không nhận dạng cá nhân như phiên bản hệ điều hành, tên đời máy, độ phân giải màn hình để tối ưu trải nghiệm chơi game.'
          }</li>
          <li><strong>${isEn ? 'Remote Configuration Requests:' : 'Yêu cầu cấu hình Remote Config:'}</strong> ${
            isEn
              ? 'Standard HTTP header timestamps served via Cloudflare Edge CDN to verify version integrity and prevent client time tampering for daily rewards.'
              : 'Dấu thời gian HTTP header từ CDN Cloudflare nhằm xác minh phiên bản cấu hình và chống gian lận thời gian nhận phần thưởng hàng ngày.'
          }</li>
        </ul>

        <h3 style="color: #fff; margin-top: 1.5rem; margin-bottom: 0.5rem;">3. Information We Do NOT Collect</h3>
        <p>We strictly do NOT collect, store, or sell:</p>
        <ul style="padding-left: 1.5rem; margin-top: 0.5rem;">
          <li>${isEn ? 'Personal identifiable information (Name, Email address, Phone number, Social security).' : 'Thông tin định danh cá nhân (Họ tên, Email, Số điện thoại).'}</li>
          <li>${isEn ? 'Real-time GPS location tracking.' : 'Vị trí địa lý GPS thời gian thực.'}</li>
          <li>${isEn ? 'Contact lists, photos, or microphone recordings.' : 'Danh bạ điện thoại, hình ảnh riêng tư hoặc micro.'}</li>
        </ul>

        <h3 style="color: #fff; margin-top: 1.5rem; margin-bottom: 0.5rem;">4. Third-Party Services</h3>
        <p>Ant Clash may utilize trusted third-party service providers for infrastructure and app store distribution:</p>
        <ul style="padding-left: 1.5rem; margin-top: 0.5rem;">
          <li><strong>Google Play Services:</strong> For Android application distribution and achievement tracking. (<a href="https://policies.google.com/privacy" target="_blank" style="color: var(--color-accent);">Google Privacy Policy</a>)</li>
          <li><strong>Cloudflare CDN:</strong> For static Remote Config content delivery. (<a href="https://www.cloudflare.com/privacypolicy/" target="_blank" style="color: var(--color-accent);">Cloudflare Privacy Policy</a>)</li>
        </ul>

        <h3 style="color: #fff; margin-top: 1.5rem; margin-bottom: 0.5rem;">5. Children's Privacy (COPPA & GDPR Compliance)</h3>
        <p>
          ${
            isEn
              ? 'Ant Clash is suitable for general audiences. We do not knowingly collect personal information from children under 13 (or under 16 in the EU). If you believe a child has provided us with personal data, please contact us immediately for deletion.'
              : 'Ant Clash phù hợp với người chơi mọi lứa tuổi. Chúng tôi không cố ý thu thập thông tin cá nhân của trẻ em dưới 13 tuổi. Nếu bạn tin rằng có dữ liệu trẻ em được gửi cho chúng tôi, vui lòng liên hệ để được hỗ trợ xóa bỏ.'
          }
        </p>

        <h3 style="color: #fff; margin-top: 1.5rem; margin-bottom: 0.5rem;">6. Data Security</h3>
        <p>We employ commercial-grade encryption and secure Cloudflare HTTPS connections for all remote configuration delivery.</p>

        <h3 style="color: #fff; margin-top: 1.5rem; margin-bottom: 0.5rem;">7. Contact Us</h3>
        <p>
          ${
            isEn
              ? 'If you have any questions or concerns regarding this Privacy Policy, please contact SNT Games Studio at:'
              : 'Nếu bạn có bất kỳ câu hỏi nào liên quan đến Chính sách quyền riêng tư này, vui lòng liên hệ SNT Games Studio qua:'
          }
        </p>
        <div style="background: var(--bg-tertiary); padding: 1rem; border-radius: 8px; border: var(--border-glass); margin-top: 0.75rem;">
          <strong>SNT Games Studio</strong><br/>
          Email: <a href="mailto:support@sntgames.com" style="color: var(--color-accent);">support@sntgames.com</a><br/>
          Website: <a href="https://sntgames.com" style="color: var(--color-accent);">https://sntgames.com</a>
        </div>
      </div>
    </div>
  `;

  container.appendChild(inner);
  return container;
};
